import {Network} from "../network/Network";
import {config} from "../config";
import {
    DateIntervalPickerPurposes
} from "../products/components/global/date/interval/picker/types/DateIntervalPickerPurposes";
import {AppearanceTheme} from "../models/enums/AppearanceTheme";
import {SedestralStorage} from "../sedestral/framework/memory/SedestralStorage";
import {
    SedestralInterface
} from "../sedestral/framework/interface/SedestralInterface";
import {txtSimpleLang} from "../sedestral/framework/utilities/TxtSimpleLang";
import {generateVendor} from "../sedestral/framework/utilities/GenerateVendor";
import {ProductType} from "../models/product/ProductType";
import {ProductName} from "../models/product/ProductName";
import {SiteWhiteLabelAgent} from "../models/site/whitelabel/SiteWhiteLabelAgent";
import {EntityService} from "../services/entity/EntityService";
import {NetworkVendor} from "../network/NetworkVendor";
import {ICurrency} from "../models/currency/ICurrency";
import {Currencies} from "../models/currency/Currencies";


export class Resources {
    public static countries: any[];
    public static emojis: any[];
    public static worldLanguages: { code: string, name: string }[];

    public static words: any;

    public static languages: string[];
    public static language: string;

    public static currency: ICurrency = Currencies.EUR;
    private static readonly europeanRegions: string[] = ["AD", "AL", "AT", "BA", "BE", "BG", "BY", "CH", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GB", "GR", "HR", "HU", "IE", "IS", "IT", "LI", "LT", "LU", "LV", "MC", "MD", "ME", "MK", "MT", "NL", "NO", "PL", "PT", "RO", "RS", "SE", "SI", "SK", "SM", "UA", "VA", "XK"];

    private static languageToCountryMap = {
        "fr": "fr",  // France
        "en": "gb",  // United Kingdom
        "es": "es",  // Spain
        "it": "it",  // Italy
        "pt": "pt",  // Portugal
        "nl": "nl",  // Netherlands
        "de": "de",  // Germany
        "lb": "lu",  // Luxembourg
        "da": "dk",  // Denmark
        "pl": "pl",  // Poland
        "ro": "ro",  // Romania
        "cs": "cz",  // Czechia
        "hu": "hu",  // Hungary
        "sk": "sk",  // Slovakia
        "bg": "bg",  // Bulgaria
        "sv": "se",  // Sweden
        "fi": "fi",  // Finland
        "no": "no",  // Norway
        "el": "gr",  // Greece
        "tr": "tr",  // Turkey
        "hr": "hr",  // Croatia
        "sl": "si",  // Slovenia
        "ja": "jp",  // Japan
        "ko": "kr",  // South Korea
        "zh": "cn",  // China
        "ar": "sa",  // Saudi Arabia
        "hi": "in",  // India
        "th": "th",  // Thailand
        "vi": "vn",  // Vietnam
        "id": "id",  // Indonesia
        "uk": "ua",  // Ukraine
        "sr": "rs",  // Serbia
        "lt": "lt",  // Lithuania
        "lv": "lv",  // Latvia
        "et": "ee",  // Estonia
        "bn": "bd",  // Bangladesh
        "ms": "my",  // Malaysia
        "he": "il",  // Israel
        "tl": "ph",  // Philippines
        "ur": "pk"   // Pakistan
    };

    static getCountryCode() {
        return this.languageToCountryMap[this.language] || "fr";
    }

    static getBrowserRegion(): string | null {
        const loc = navigator.languages?.[0] ?? navigator.language;
        const parts = loc?.split("-");
        if (parts && parts.length > 1) {
            return parts[1].toUpperCase();
        }

        // locale sans pays (ex "en", "pl") → défaut par langue pour la devise de la page vitrine :
        // en → US (norme), pl → Pologne. Les autres langues restent null → EUR côté back (resolve).
        const language = parts?.[0]?.toLowerCase();
        if (language === "en") {
            return "US";
        }
        if (language === "pl") {
            return "PL";
        }
        return null;
    }

    static isBrowserRegionEuropean(): boolean {
        const region = this.getBrowserRegion();
        return region != null && this.europeanRegions.includes(region);
    }

    public static async init(languagesCandidate: string[], languages: string[], defaultLanguage = this.getDefaultLanguage()): Promise<void> {
        this.languages = languages;

        let selectBestLanguageCandidate = this.getBestLanguageCandidate(languages, languagesCandidate);

        this.setLanguage(selectBestLanguageCandidate);

        if (!this.language) {
            this.setLanguage(defaultLanguage);
        }

        if (!this.language) {
            this.setLanguage(this.getDefaultLanguage());
        }

        if (config.product == "blog")
            this.theme(ProductType.BLOG) == AppearanceTheme.LIGHT ? SedestralInterface.html.removeClass("black") : SedestralInterface.html.addClass("black");
    }

    public static async loadI18n(product: string, vendor: string = generateVendor(window.location.origin)): Promise<void> {
        const request = await Network.httpResources.get(vendor + "languages/" + product + "/" + this.language + ".json?" + config.version, {timeout: 60000});

        if (this.words !== undefined) {
            this.words = this.mergeI18n(this.words, request.data);
        } else {
            this.words = request.data;

            Network.initErrors();
            DateIntervalPickerPurposes.init();
        }
    }

    public static async loadCountries(vendor: string = generateVendor(window.location.origin)): Promise<void> {
        let request = await Network.httpResources.get(vendor + "countries/" + this.language + ".json?" + config.version, {timeout: 60000});
        this.countries = request.data;
    }

    public static async loadEmojis(vendor: string = generateVendor(window.location.origin)): Promise<any> {
        if (this.emojis != undefined) {
            return this.emojis;
        }

        let request = await Network.httpResources.get(vendor + "emojis/emojis.json?" + config.version);
        this.emojis = request.data;
        return this.emojis;
    }

    public static async loadWorldLanguages(vendor: string = generateVendor(window.location.origin)): Promise<void> {
        let request = await Network.httpResources.get(vendor + "languages-names/" + this.language + ".json?" + config.version, {timeout: 60000});
        this.worldLanguages = request.data;
    }

    public static t(value, words?: any) {
        const values = value.split(".");
        let object = words ?? this.words;

        for (const val of values) {
            if (object) {
                object = object[val];
            }
        }

        return object ?? value;
    }

    public static r(value, replacements, words = this.words) {
        const values = value.split(".");
        let object = words;

        for (const val of values) {
            if (typeof object !== "undefined") {
                object = object[val];
            }
        }

        let result = (typeof object === "undefined" || typeof object === "object") ? value : object;

        for (const key in replacements) {
            result = result.replace(`\${${key}}`, replacements[key]);
        }

        return result;
    }

    public static a(value, extraReplacements?: Record<string, string>, words?: any): string {
        const site = EntityService.activeSite;
        const agentNames: Record<string, string> = {
            sedestral: site?.whiteLabel && site?.whiteLabelData?.agencyName || "Sedestral",
            alya: site?.whiteLabel && site?.whiteLabelData?.agentNames?.["alya"] || "Alya",
            maya: site?.whiteLabel && site?.whiteLabelData?.agentNames?.["maya"] || "Maya",
            nox: site?.whiteLabel && site?.whiteLabelData?.agentNames?.["nox"] || "Nox",
            remi: site?.whiteLabel && site?.whiteLabelData?.agentNames?.["remi"] || "Rémi",
            marc: site?.whiteLabel && site?.whiteLabelData?.agentNames?.["marc"] || "Marc",
        };
        const replacements = extraReplacements ? {...agentNames, ...extraReplacements} : agentNames;

        const values = value.split(".");
        let object = words ?? this.words;

        for (const val of values) {
            if (typeof object !== "undefined") {
                object = object[val];
            }
        }

        let result = (typeof object === "undefined" || typeof object === "object") ? value : object;

        for (const key in replacements) {
            result = result.replaceAll(`\${${key}}`, replacements[key]);
        }

        return result;
    }

    public static exist(value, words?: any): boolean {
        const values = value.split(".");
        let object = words ?? this.words;

        for (const val of values) {
            if (object) {
                object = object[val];
            }
        }

        return object !== undefined;
    }

    public static countryFrom(a2: string): { id: number, n: string, a2: string, a3: string } {
        a2 = a2.toLowerCase();
        return this.countries.filter(value => value['a2'] == a2)[0];
    }

    public static lang(code: string): { code: string, name: string } {
        code = txtSimpleLang(code);
        for (let key in this.worldLanguages) {
            if (this.worldLanguages.hasOwnProperty(key)) {
                if (this.worldLanguages[key].code == code) {
                    return this.worldLanguages[key];
                }
            }
        }
    }

    public static getLanguagesCandidate(): string[] {
        return [...new Set(navigator.languages.map(language => this.removeRegionLanguage(language)))];
    }

    public static getBestLanguageCandidate(languages: string[], languagesCandidate: string[] = this.getLanguagesCandidate()) {
        return languagesCandidate.find(language => languages.includes(language));
    }

    public static getDefaultLanguage() {
        return "en";
    }

    private static setLanguage(language) {
        if (this.languages.includes(language)) {
            this.language = language;
        }
    }

    private static removeRegionLanguage(language: string): string {
        if (language == undefined) {
            return undefined;
        }

        const withoutQuality: string = language.split(";")[0];

        if (withoutQuality.includes("-")) {
            return withoutQuality.split("-")[0];
        }
        if (withoutQuality.includes("_")) {
            return withoutQuality.split("_")[0];
        }

        return withoutQuality;
    }

    public static theme(productType: ProductType): AppearanceTheme {
        let theme = this.realTheme(productType);
        if (theme == AppearanceTheme.AUTO) {
            if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                return AppearanceTheme.DARK;
            } else {
                return AppearanceTheme.LIGHT;
            }
        }

        return theme;
    };

    public static realTheme(productType: ProductType): AppearanceTheme {
        let theme = SedestralStorage.getItem(`${ProductName.toString(productType)}-theme`) as any;
        if (theme == null) {
            return AppearanceTheme.AUTO;
        }

        return parseInt(theme);
    }

    private static mergeI18n(wordsA: any, wordsB: any): any {
        return {
            ...wordsA,
            ...wordsB,
            words: {...wordsA.words, ...wordsB.words},
            network: {...wordsA.network, ...wordsB.network},
            errors: {...wordsA.errors, ...wordsB.errors},
            placeholders: {...wordsA.placeholders, ...wordsB.placeholders},
            sentences: {...wordsA.sentences, ...wordsB.sentences},
            products: {...wordsA.products, ...wordsB.products},
            time: {...wordsA.time, ...wordsB.time}
        };
    }

    public static getGuide(): { path: string, target?: boolean, language?: string } {
        switch (Network.router.pages.getLanguage()) {
            case "fr":
                return {target: false, path: Network.router.pages.getUrlWithPath("guide/seo-naturel"), language: "fr"};
            case "es":
                return {
                    target: false,
                    path: Network.router.pages.getUrlWithPath("guide/introduccion-al-seo"),
                    language: "es"
                };
            case "en":
                return {
                    target: false,
                    path: Network.router.pages.getUrlWithPath("guide/seo-for-beginners"),
                    language: "en"
                };
            case "it":
                return {
                    target: false,
                    path: Network.router.pages.getUrlWithPath("guide/seo-per-principianti"),
                    language: "it"
                };
            default:
                return {
                    target: true,
                    path: Network.router.pages.getUrlWithLangAndPath("en", "guide/seo-for-beginners"),
                    language: "en"
                };
        }
    }

    public static getBlog(): { path: string, target?: boolean, language?: string } {
        const language = Network.router.pages.getLanguage();

        if (config.supportedBlogLanguages.includes(language)) {
            return {target: false, path: Network.router.pages.getUrlWithPath("blog"), language: language};
        }

        return {
            target: true,
            path: Network.router.pages.getUrlWithLangAndPath("fr", "blog"),
            language: "fr"
        };
    }

    public static getVideoPresentation() {
        switch (Network.router.pages.getLanguage()) {
            case "fr":
                return "https://www.youtube.com/embed/1M7qR7U3NWw?si=jDOwbM49VSxmOrVD"; //web
            case "en":
                return "https://www.youtube.com/embed/ClExKxewM30";
            case "es":
                return "https://www.youtube.com/embed/VdHopvhuJ2o?si=Bqm3YiP9IlhCM-4Y"; //web
            case "it":
                return "https://www.youtube.com/embed/i2owjyxlcic";
            case "de":
                return "https://www.youtube.com/embed/d4-uu5XpIQU";
            case "pl":
                return "https://www.youtube.com/embed/4_Caj6dryTY";
            default:
                return "https://www.youtube.com/embed/1M7qR7U3NWw?si=jDOwbM49VSxmOrVD";
        }
    }

    public static getWixVideo(): string {
        if (Network.router.pages.getLanguage() == "fr")
            return "https://www.youtube.com/embed/N9qAwGJUZYI?si=OagClyUTHdVb-44H";

        return this.getVideo();
    }

    public static getExplainVideo(type: string): string {
        const language = Network.router.pages.getLanguage();
        const videos: Record<string, Record<string, string>> = {
            "alya": {
                "fr": "https://www.youtube.com/watch?v=4KKreoYYAb8",
                "de": "https://youtu.be/_-htoYHR8Xw",
                "it": "https://youtu.be/i7P30nfnsjc",
                "es": "https://youtu.be/_5PK5BNUWQk",
                "en": "https://youtu.be/VqzTTvkFsSU",
            },
            "maya": {
                "fr": "https://www.youtube.com/watch?v=pQ5cNlBNp3c",
                "es": "https://youtu.be/otM-y3-UV-M",
                "it": "https://youtu.be/HysGGL-9VRA",
                "de": "https://youtu.be/Mi0O4-fGlwY",
                "en": "https://youtu.be/Vf7vqoE3XWI",
            },
            "marc": {
                "fr": "https://www.youtube.com/watch?v=9F7YCXdvUpA",
                "es": "https://youtu.be/OERQiLgfofA",
                "it": "https://youtu.be/12TcOTWrYIg",
                "en": "https://youtu.be/slaVLXDlj5M",
                "de": "https://youtu.be/eij1cYen1aQ",
            },
            "remi": {
                "fr": "https://www.youtube.com/watch?v=qaQH9CTdjfc",
                "en": "https://youtu.be/7gN8nCDy0W8",
                "es": "https://youtu.be/qqZy7le0Sqg",
                "de": "https://youtu.be/iP49AIO5394",
                "it": "https://youtu.be/ccfvcalZlgU",
            },
            "blog": {
                "fr": "https://www.youtube.com/watch?v=4KKreoYYAb8",
                "es": "https://youtu.be/_5PK5BNUWQk",
                "it": "https://youtu.be/i7P30nfnsjc",
                "en": "https://youtu.be/VqzTTvkFsSU",
                "de": "https://youtu.be/_-htoYHR8Xw",
            },
        };

        const typeVideos = videos[type] ?? videos["alya"];
        return typeVideos[language] ?? typeVideos["en"] ?? typeVideos["fr"];
    }

    public static getExplainVideoThumbnail(type: string): string {
        switch (type) {
            case "remi":
                return "thumbnail-remi.png";
            case "maya":
                return "thumbnail-maya.png";
            case "marc":
                return "thumbnail-marc.png";
            case "blog":
                return "thumbnail-blog.png";
            default:
                return "thumbnail-remi.png";
        }
    }

    public static getVideo(): string {
        switch (Network.router.pages.getLanguage()) {
            case "fr":
                return "https://www.youtube.com/embed/eXRyW-JvlSg?si=nYThJMoX4h9VLgt1";
            case "it":
                return "https://www.youtube.com/embed/62ZaRKqyE44?si=7JBQiF6G73vA9UZj";
            case "es":
                return "https://www.youtube.com/embed/xHP3W5xHbso?si=k5td6_4AtA5lqI9V";
            default:
                return "https://www.youtube.com/embed/eXRyW-JvlSg?si=nYThJMoX4h9VLgt1";
        }
    }

    public static getVideoAgents(): string {
        switch (Network.router.pages.getLanguage()) {
            case "fr":
                return "https://youtu.be/lmUCLdSNBDk";
            case "en":
                return "https://youtu.be/TR84Z3booes";
            case "it":
                return "https://youtu.be/9zfIjXXxjQA";
            case "es":
                return "https://youtu.be/W5KGuztc234";
            default:
                return "https://youtu.be/TR84Z3booes";
        }
    }

    public static agentHasCustomImage(agent: SiteWhiteLabelAgent): boolean {
        const site = EntityService.activeSite;
        return !!(site?.whiteLabel && site?.whiteLabelData?.agentAvatarFileIds?.[agent]);
    }

    public static agentImageUrl(agent: SiteWhiteLabelAgent): string {
        const site = EntityService.activeSite;
        if (site?.whiteLabel && site?.whiteLabelData?.agentAvatarFileIds?.[agent]) {
            return `${config.httpPanelUrl}/files/${site.whiteLabelData.agentAvatarFileIds[agent]}.png`;
        }
        return NetworkVendor.image(`agents/${agent}.png`);
    }

    private static agentClassMap: Record<string, SiteWhiteLabelAgent> = {
        "alya": SiteWhiteLabelAgent.AGENT_ALYA,
        "maya": SiteWhiteLabelAgent.AGENT_MAYA,
        "remi": SiteWhiteLabelAgent.AGENT_REMI,
        "nox": SiteWhiteLabelAgent.AGENT_NOX,
        "marc": SiteWhiteLabelAgent.AGENT_MARC,
        "louise": SiteWhiteLabelAgent.NAV_LOUISE,
        "marcBig": SiteWhiteLabelAgent.AGENT_MARC,
        "marcMid": SiteWhiteLabelAgent.AGENT_MARC,
        "mayaBottom": SiteWhiteLabelAgent.AGENT_BOTTOM_MAYA,
        "mayaBottomAlpha": SiteWhiteLabelAgent.AGENT_BOTTOM_MAYA,
        "mayaBottomMid": SiteWhiteLabelAgent.AGENT_BOTTOM_MAYA,
        "remiBottom": SiteWhiteLabelAgent.AGENT_BOTTOM_REMI,
        "remiGoogle": SiteWhiteLabelAgent.AGENT_GOOGLE_REMI,
        "googleRemi": SiteWhiteLabelAgent.AGENT_GOOGLE_REMI,
        "alyaWin": SiteWhiteLabelAgent.AGENT_WIN_ALYA,
        "winAlya": SiteWhiteLabelAgent.AGENT_WIN_ALYA,
        "teamWhite": SiteWhiteLabelAgent.AGENT_TEAM_WHITE,
        "team": SiteWhiteLabelAgent.AGENT_TEAM,
        "luca": SiteWhiteLabelAgent.NAV_LUCA,
        "jule": SiteWhiteLabelAgent.NAV_JULE,
        "chris": SiteWhiteLabelAgent.NAV_CHRIS,
        "max": SiteWhiteLabelAgent.NAV_MAX,
    };

    public static agentClassImageUrl(className: string): string {
        const agent = this.agentClassMap[className];
        if (!agent) return "";
        return Resources.agentImageUrl(agent);
    }
}